import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { assetsCollection } from './firestoreRefs';

const buildCanvas = async ({ element, backgroundColor = '#ffffff', scale = 3 }) => {
  if (!element) {
    throw new Error('Export element missing.');
  }
  return html2canvas(element, {
    scale,
    backgroundColor,
    useCORS: true,
    logging: false,
  });
};

export const exportDomToPdf = async ({
  element,
  fileName,
  pageSizeMm,
  backgroundColor = '#ffffff',
  marginMm = 0,
}) => {
  const canvas = await buildCanvas({ element, backgroundColor, scale: 3 });
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: [pageSizeMm.width, pageSizeMm.height],
  });

  const printableWidth = pageSizeMm.width - marginMm * 2;
  const printableHeight = pageSizeMm.height - marginMm * 2;
  const imageData = canvas.toDataURL('image/png', 1.0);
  pdf.addImage(imageData, 'PNG', marginMm, marginMm, printableWidth, printableHeight);

  const outputBlob = pdf.output('blob');
  if (fileName) {
    pdf.save(fileName);
  }
  return outputBlob;
};

export const exportDomToPng = async ({ element, backgroundColor = '#ffffff', scale = 3 }) => {
  const canvas = await buildCanvas({ element, backgroundColor, scale });
  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (result) => {
        if (!result) {
          reject(new Error('Unable to create PNG.'));
          return;
        }
        resolve(result);
      },
      'image/png',
      1.0,
    );
  });
  return blob;
};

const WORD_EXPORT_STYLES = `
  body {
    margin: 0;
    font-family: "Times New Roman", serif;
    color: #111111;
  }
  .printable-page {
    width: 100%;
    box-sizing: border-box;
    background: #ffffff;
  }
  .page-inner {
    min-height: 1000px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 32px;
    box-sizing: border-box;
  }
  .printable-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
  }
  .printable-logo img {
    max-width: 180px;
    height: auto;
  }
  .letterhead-body {
    min-height: 620px;
  }
  .printable-footer {
    border-top: 1px solid #000000;
    padding-top: 12px;
    font-size: 12px;
    line-height: 1.4;
  }
`;

export const exportDomToWord = ({ element }) => {
  if (!element) {
    throw new Error('Export element missing.');
  }

  const html = `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <style>${WORD_EXPORT_STYLES}</style>
  </head>
  <body>${element.outerHTML}</body>
</html>`;

  return new Blob(['\ufeff', html], { type: 'application/msword' });
};

export const downloadBlob = (blob, filename) => {
  if (!blob) {
    throw new Error('Missing download blob.');
  }
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || 'download';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

export const uploadBlobToStorage = async ({
  firebase,
  storagePath,
  blob,
  contentType,
  metadata = {},
}) => {
  if (!firebase?.storage) {
    throw new Error('Storage not available.');
  }
  const ref = firebase.storage.ref().child(storagePath);
  const uploadTask = await ref.put(blob, {
    contentType,
    customMetadata: metadata,
  });
  return uploadTask.ref.getDownloadURL();
};

export const createAssetProcessingDoc = async ({
  firebase,
  tenantId,
  branchId,
  authUserId,
  type,
  snapshot,
  extra = {},
}) => {
  const assetRef = assetsCollection(tenantId).doc();
  const now = firebase.fieldValue.serverTimestamp();
  await assetRef.set({
    type,
    tenantId,
    branchId,
    createdBy: authUserId,
    createdAt: now,
    updatedAt: now,
    status: 'processing',
    snapshot,
    ...extra,
  });
  return assetRef;
};

export const finalizeAssetDoc = async ({ firebase, assetRef, output, extra = {} }) => {
  if (!assetRef) return;
  await assetRef.set(
    {
      status: 'ready',
      updatedAt: firebase.fieldValue.serverTimestamp(),
      output,
      ...extra,
    },
    { merge: true },
  );
};

export const failAssetDoc = async ({ firebase, assetRef, errorMessage }) => {
  if (!assetRef) return;
  await assetRef.set(
    {
      status: 'failed',
      updatedAt: firebase.fieldValue.serverTimestamp(),
      errorMessage: errorMessage || 'Export failed',
    },
    { merge: true },
  );
};
